import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { getSubmission, reviewSubmission, reviewSubmissionWithProgress, getUserProgress, getAllModules } from '../lib/firestore';

const ReviewPage = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submission, setSubmission] = useState(null);
  const [reviewData, setReviewData] = useState({
    status: 'approved',
    feedback: ''
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchSubmission = useCallback(async () => {
    try {
      const sub = await getSubmission(submissionId);
      setSubmission(sub);
    } catch (error) {
      toast.error('Failed to load submission');
    } finally {
      setLoading(false);
    }
  }, [submissionId]);

  useEffect(() => {
    fetchSubmission();
  }, [fetchSubmission]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // If approved, update student progress
      if (reviewData.status === 'approved') {
        const studentProgress = await getUserProgress(submission.studentId);
        const modules = await getAllModules();
        const sortedModules = modules.sort((a, b) => a.order - b.order);
        
        const completedModuleIds = [...(studentProgress.completedModuleIds || [])];
        if (!completedModuleIds.includes(submission.moduleId)) {
          completedModuleIds.push(submission.moduleId);
        }
        
        // Unlock next module
        const currentModuleIndex = sortedModules.findIndex(m => m.id === submission.moduleId);
        const unlockedModuleIds = [...(studentProgress.unlockedModuleIds || [])];
        
        if (currentModuleIndex >= 0 && currentModuleIndex < sortedModules.length - 1) {
          const nextModule = sortedModules[currentModuleIndex + 1];
          if (!unlockedModuleIds.includes(nextModule.id)) {
            unlockedModuleIds.push(nextModule.id);
          }
        }

        const finalModule = sortedModules[sortedModules.length - 1];
        const careerLaunchEligible = Boolean(
          finalModule &&
          (completedModuleIds.length >= sortedModules.length || completedModuleIds.includes(finalModule.id))
        );

        await reviewSubmissionWithProgress(
          submissionId,
          reviewData.status,
          reviewData.feedback,
          user.id,
          submission.studentId,
          completedModuleIds,
          unlockedModuleIds,
          careerLaunchEligible
        );
      } else {
        await reviewSubmission(submissionId, reviewData.status, reviewData.feedback, user.id);
      }
      
      toast.success('Review submitted successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Review error:', error);
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
        <div className="text-[#00E5FF] text-xl">Loading...</div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
        <div className="text-[#EF4444] text-xl">Submission not found</div>
      </div>
    );
  }

  return (
    <div data-testid="review-page" className="min-h-screen bg-[#0B0E14] pb-12">
      <div className="bg-[#151923] border-b border-[#1E293B]">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <Button
            data-testid="back-btn"
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4 text-[#00E5FF] hover:text-[#33EBFF] hover:bg-[#1E293B]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Review Submission
          </h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Submission Details */}
          <div>
            <div className="bg-[#151923] border border-[#1E293B] rounded-md p-6 mb-6">
              <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Submission Details
              </h2>
              <div className="space-y-3">
                <div>
                  <div className="text-sm text-[#94A3B8]">Student</div>
                  <div className="text-[#F8FAFC] font-medium">{submission.studentName}</div>
                </div>
                <div>
                  <div className="text-sm text-[#94A3B8]">Module</div>
                  <div className="text-[#F8FAFC] font-medium">{submission.moduleTitle}</div>
                </div>
                <div>
                  <div className="text-sm text-[#94A3B8]">Submitted</div>
                  <div className="text-[#F8FAFC] font-mono text-sm">
                    {submission.createdAt?.toDate ? submission.createdAt.toDate().toLocaleString() : 'Recently'}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#151923] border border-[#1E293B] rounded-md p-6">
              <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Student's Answer
              </h2>
              <div className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-4 mb-4">
                <p className="text-[#F8FAFC] whitespace-pre-wrap">
                  {submission.textAnswer || 'No text answer provided'}
                </p>
              </div>

              {submission.links?.length > 0 && (
                <div className="mb-4">
                  <div className="text-sm font-semibold text-[#00E5FF] mb-2">Links</div>
                  <div className="space-y-1">
                    {submission.links.map((link, idx) => (
                      <a
                        key={idx}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#6366F1] hover:text-[#818CF8] underline block text-sm"
                      >
                        {link}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {submission.fileUrls?.length > 0 && (
                <div>
                  <div className="text-sm font-semibold text-[#00E5FF] mb-2">Uploaded Files</div>
                  <div className="space-y-1">
                    {submission.fileUrls.map((file, idx) => (
                      <a key={idx} href={file.url} target="_blank" rel="noopener noreferrer" className="text-[#6366F1] hover:text-[#818CF8] underline block text-sm">
                        {file.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Review Form */}
          <div>
            <div className="bg-[#151923] border border-[#1E293B] rounded-md p-6 sticky top-6">
              <h2 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Submit Review
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-[#F8FAFC]">Status</Label>
                  <Select
                    value={reviewData.status}
                    onValueChange={(value) => setReviewData({ ...reviewData, status: value })}
                  >
                    <SelectTrigger data-testid="review-status-select" className="bg-[#0B0E14] border-[#1E293B] text-[#F8FAFC] mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#151923] border-[#1E293B]">
                      <SelectItem value="approved" className="text-[#F8FAFC]">Approved</SelectItem>
                      <SelectItem value="rejected" className="text-[#F8FAFC]">Needs Revision</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="feedback" className="text-[#F8FAFC]">Feedback</Label>
                  <Textarea
                    data-testid="review-feedback-textarea"
                    id="feedback"
                    value={reviewData.feedback}
                    onChange={(e) => setReviewData({ ...reviewData, feedback: e.target.value })}
                    className="bg-[#0B0E14] border-[#1E293B] text-[#F8FAFC] mt-2 min-h-[200px]"
                    placeholder="Provide detailed feedback on the submission..."
                    required
                  />
                </div>

                <div className="pt-4">
                  <Button
                    data-testid="submit-review-btn"
                    type="submit"
                    className="w-full bg-[#00E5FF] text-black hover:bg-[#33EBFF] font-semibold py-6 rounded-md"
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit Review'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewPage;
